import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { More } from "./radarUi";
import { useRadarProfile } from "hooks/useRadarData";
import { rtctApi } from "services/rtctApi";
import { formatMm as mm, sgtTime as sgt } from "utils/radarDiagnostics";
import {
  FLOOR_MM,
  NOTES_MAX,
  PIPE_MM,
  STATES,
  learnPayload,
  learnWindow,
  profileFacts,
  readProfile,
} from "utils/radarAnalysis";
import {
  MAX_WATER_MM,
  NEAR_MM,
  gateBasisText,
  gateFloorMm,
  gateMatchText,
  gateText,
  globalNearMm,
  readSuggestedGate,
  windowGate,
  windowOf,
  windowPayload,
  windowText as searchText,
} from "utils/radarEdge";
import { windowText } from "utils/radarStream";
import { PIPE_ABOVE_SOIL_MM, SOIL_MM, soilPayload, soilReference, topPayload } from "utils/radarSoil";

// Commissioning (Radar analysis): the node's empty-pipe profile. Our
// estimate needs to know what this pipe looks like with no water in it, so
// the bursts of a time when it was empty are learned as its "clutter map"
// (POST radar/profile/ {pipe_length_mm, expected_floor_mm?, notes?,
// dry_from, dry_to, confirm: true}; without the window the lengths and notes
// are saved and a map learned before is kept). Either replaces the profile
// in use, so it is sent only after a confirm that says what it replaces; the
// server keeps the one replaced, and Undo (POST radar/profile/undo/) puts it
// back. The bursts are picked on the analysis timeline's points (the page's
// window); the profile as the server keeps it is shown with its quality,
// warnings and the earlier profiles. From the backend's .62 support the
// profile also keeps the node's own search window for estimator v2 (near_mm,
// max_water_mm: null for the global setting), saved on its own - the
// profile's lengths and notes as the server keeps them, no dry window, so the
// map is kept and nothing is learned again - and each answer suggests the
// gate the node's own estimate should search (suggested_gate), pushed from
// Node Control. From the backend's soil support it also keeps the field's
// soil surface over the pipe's floor (soil_above_floor_mm), saved on its own
// in the same way: the server gives each reading against it (the water's
// depth for alternate wetting and drying) as it serves it, so nothing is
// analysed again.
// The server learns a map from at least this many bursts (at most 200).
export const LEARN_MIN = 3;
const W = 600;
const H = 40;
const siteFloor = (mac) => {
  try {
    const v = JSON.parse(localStorage.getItem(`field_radar:${mac}`) || "{}");
    return v && Number(v.floor_mm) > 0 ? String(Math.round(v.floor_mm)) : "";
  } catch {
    return "";
  }
};
const lengthText = (v) => (v === null ? "" : String(Math.round(v)));
const plural = (n, word) =>
  `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;
// A profile (or an earlier one) in words: its map's bursts and window, or
// none, and its lengths.
const mapText = (f) =>
  f.commissioned
    ? `map from ${f.bursts !== null ? plural(f.bursts, "burst") : "bursts"}${f.from && f.to ? `, ${sgt(f.from)} to ${sgt(f.to)}` : ""}`
    : "no map (lengths only)";
// The lengths, and the search window and soil surface where they are set:
// "pipe 1,200 mm, expected floor 1,173 mm, search from 300 mm, soil surface
// 300 mm over the floor".
const lengthsText = ({
  pipeLengthMm,
  expectedFloorMm,
  nearMm = null,
  maxWaterMm = null,
  soilAboveFloorMm = null,
}) =>
  [
    pipeLengthMm !== null ? `pipe ${mm(pipeLengthMm)}` : null,
    expectedFloorMm !== null
      ? `expected floor ${mm(expectedFloorMm)}`
      : "no expected floor",
    searchText({ nearMm, maxWaterMm }) || null,
    soilAboveFloorMm !== null
      ? `soil surface ${mm(soilAboveFloorMm)} over the floor`
      : null,
  ]
    .filter(Boolean)
    .join(", ");
const profileText = (f) =>
  [f.updatedAt ? `saved ${sgt(f.updatedAt)}` : null, mapText(f), lengthsText(f)]
    .filter(Boolean)
    .join(" · ");
const newer = (a, b) =>
  (Date.parse(a?.updated_at) || 0) >= (Date.parse(b?.updated_at) || 0);

// The bursts as ticks across the window, coloured by our state, with the
// chosen ones shaded.
function Strip({ points, from, to, lo, hi }) {
  const x = (ms) => ((ms - from) / Math.max(1, to - from)) * W;
  const a = points[lo];
  const b = points[hi];
  return (
    <svg
      className="field-commission-strip"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {a && b && (
        <rect
          x={x(a.ms) - 3}
          y={0}
          width={Math.max(6, x(b.ms) - x(a.ms) + 6)}
          height={H}
          rx={4}
          className="field-commission-chosen"
        />
      )}
      {points.map((p, i) => (
        <line
          key={`${p.ms}-${i}`}
          x1={x(p.ms)}
          x2={x(p.ms)}
          y1={8}
          y2={H - 8}
          stroke={STATES[p.state].color}
          strokeWidth={i >= lo && i <= hi ? 2.5 : 1.5}
          strokeOpacity={i >= lo && i <= hi ? 1 : 0.45}
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
}

export default function RadarCommissioning({
  mac,
  points = [],
  available = true,
  minutes = 1440,
  refresh = 0,
  onLearned,
  // The timeline is thinned out (rich readings over 7 or 30 days): its
  // points are some of the window's bursts, and the server learns from all
  // of the bursts in the window picked.
  thinned = false,
}) {
  const [tick, setTick] = useState(0);
  const loaded = useRadarProfile(mac, refresh + tick);
  const [learned, setLearned] = useState(null);
  // The gate the server suggested with that answer (undefined: none yet).
  const [learnedGate, setLearnedGate] = useState(undefined);
  // The profile the server answered a change with, until it serves one as
  // new or newer.
  const profile = !learned
    ? loaded.profile
    : loaded.profile && newer(loaded.profile, learned)
      ? loaded.profile
      : learned;
  const facts = profileFacts(profile);
  const gate =
    learned && profile === learned && learnedGate !== undefined
      ? learnedGate
      : loaded.suggestedGate;
  const missing = loaded.error?.status === 404 && !available;
  const [pipeText, setPipeText] = useState("");
  const [floorText, setFloorText] = useState(() => siteFloor(mac));
  const [notesText, setNotesText] = useState("");
  // The search window: blank is the global setting (near) or no limit.
  const [nearText, setNearText] = useState("");
  const [deepText, setDeepText] = useState("");
  const [windowError, setWindowError] = useState("");
  // The soil surface over the pipe's floor: blank is none.
  const [soilText, setSoilText] = useState("");
  const [soilError, setSoilError] = useState("");
  const [topText, setTopText] = useState("");
  const [topError, setTopError] = useState("");
  const filled = useRef(false);
  const [chosen, setChosen] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  // The change being confirmed: "learn", "save" or "undo".
  const [asking, setAsking] = useState(null);
  const cancelButton = useRef(null);
  useEffect(() => {
    if (asking) cancelButton.current?.focus();
  }, [asking]);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  // The inputs start from the profile the server keeps.
  useEffect(() => {
    if (filled.current || !facts) return;
    filled.current = true;
    if (facts.pipeLengthMm !== null)
      setPipeText(lengthText(facts.pipeLengthMm));
    if (facts.expectedFloorMm !== null)
      setFloorText(lengthText(facts.expectedFloorMm));
    if (facts.notes) setNotesText(facts.notes);
    setNearText(lengthText(facts.nearMm));
    setDeepText(lengthText(facts.maxWaterMm));
    setSoilText(lengthText(facts.soilAboveFloorMm));
    setTopText(lengthText(facts.pipeAboveSoilMm));
  }, [facts]);

  // The bursts chosen, by time, so new bursts arriving do not move them: the
  // profile's own window when it lies in view, else the newest twelve.
  const first = (ms) => points.findIndex((p) => p.ms >= ms);
  const last = (ms) => {
    for (let i = points.length - 1; i >= 0; i--)
      if (points[i].ms <= ms) return i;
    return -1;
  };
  const range = useMemo(() => {
    if (!points.length) return null;
    const pick = chosen || {
      from: facts?.from ? Date.parse(facts.from) : null,
      to: facts?.to ? Date.parse(facts.to) : null,
    };
    let lo = pick.from !== null ? first(pick.from) : -1;
    let hi = pick.to !== null ? last(pick.to) : -1;
    if (lo < 0 || hi < 0 || hi < lo) {
      hi = points.length - 1;
      lo = Math.max(0, hi - 11);
    }
    return { lo, hi };
    // first/last read `points` only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, chosen, facts?.from, facts?.to]);
  const picked = range ? learnWindow(points, range.lo, range.hi) : null;
  const choose = (lo, hi) => {
    setChosen({ from: points[lo].ms, to: points[hi].ms });
    setAsking(null);
  };
  const wet = picked?.states?.wet || 0;
  // The profile's own bursts lie before the window shown.
  const before =
    facts?.to && points.length && Date.parse(facts.to) < points[0].ms;
  const to = points.length ? points[points.length - 1].ms : Date.now();
  const from = Math.min(
    points.length ? points[0].ms : to,
    to - minutes * 60000,
  );

  // Learn (with the window) or save the lengths and notes only: checked
  // here first, then confirmed, then sent with confirm: true.
  const payloadOf = (commission) =>
    learnPayload({
      pipeText,
      floorText,
      notesText,
      window: picked,
      commission,
    });
  const ask = (kind) => {
    setDone(null);
    if (kind !== "undo") {
      const payload = payloadOf(kind === "learn");
      if (payload.error) {
        setError(payload.error);
        setAsking(null);
        return;
      }
    }
    setError("");
    setAsking(kind);
  };
  const send = async (commission) => {
    const payload = payloadOf(commission);
    if (payload.error) {
      setError(payload.error);
      setAsking(null);
      return;
    }
    setBusy(commission ? "learn" : "save");
    setError("");
    setDone(null);
    const existed = !!facts;
    try {
      const data = await rtctApi.learnRadarProfile(mac, {
        ...payload.body,
        confirm: true,
      });
      if (!alive.current) return;
      const saved = readProfile(data);
      setLearned(saved);
      setLearnedGate(readSuggestedGate(data));
      setAsking(null);
      setDone({
        kind: commission ? "learn" : "save",
        existed,
        bursts: profileFacts(saved)?.bursts ?? null,
        kept: existed && !!profileFacts(saved)?.canUndo,
      });
      setTick((n) => n + 1);
      onLearned?.();
    } catch (e) {
      if (alive.current) {
        setAsking(null);
        setError(
          e.status === 404
            ? "This server cannot keep an empty-pipe profile yet; it needs the updated backend."
            : `${commission ? "The profile was not learned" : "The profile was not saved"}: ${e.message || "the server refused it."}`,
        );
      }
    } finally {
      if (alive.current) setBusy("");
    }
  };
  // Put back the profile the last change replaced.
  const undo = async () => {
    setBusy("undo");
    setError("");
    setDone(null);
    try {
      const data = await rtctApi.undoRadarProfile(mac);
      if (!alive.current) return;
      const back = readProfile(data);
      setLearned(back);
      setLearnedGate(readSuggestedGate(data));
      setAsking(null);
      setWindowError("");
      setSoilError("");
      // The inputs show the profile put back.
      const backFacts = profileFacts(back);
      if (backFacts) {
        setPipeText(lengthText(backFacts.pipeLengthMm));
        setFloorText(lengthText(backFacts.expectedFloorMm));
        setNotesText(backFacts.notes);
        setNearText(lengthText(backFacts.nearMm));
        setDeepText(lengthText(backFacts.maxWaterMm));
        setSoilText(lengthText(backFacts.soilAboveFloorMm));
        setTopText(lengthText(backFacts.pipeAboveSoilMm));
      }
      const restored = data?.restored || {};
      setDone({
        kind: "undo",
        replacedAt: restored.replaced_at || null,
        bursts: Number.isFinite(Number(restored.clutter_n))
          ? Number(restored.clutter_n)
          : null,
      });
      setTick((n) => n + 1);
      onLearned?.();
    } catch (e) {
      if (alive.current) {
        setAsking(null);
        setError(
          `The earlier profile was not put back: ${e.message || "the server refused it."}`,
        );
      }
    } finally {
      if (alive.current) setBusy("");
    }
  };

  // ---- The search window (the backend's .62 support) ----
  // Saved alone: the profile's lengths and notes as the server keeps them,
  // no dry window (so the map is kept) and the window, each end null for the
  // global setting; a window whose gate would be empty is refused here as
  // the server refuses it.
  const windowKept = loaded.windowKept || windowOf(profile).kept;
  const windowNow = {
    nearMm: facts?.nearMm ?? null,
    maxWaterMm: facts?.maxWaterMm ?? null,
  };
  const globalNear = globalNearMm(profile, gate);
  const gateAt = (w) =>
    windowGate({
      ...w,
      floorMm: gateFloorMm(profile),
      endMm: gate?.gateMm ? gate.gateMm[1] : null,
      globalNearMm: globalNear,
    });
  const searchNow = gate?.gateMm
    ? { from: gate.gateMm[0], to: gate.gateMm[1], empty: false }
    : gateAt(windowNow);
  const windowDraft = windowPayload({ nearText, maxText: deepText });
  const windowNew = windowDraft.body
    ? {
        nearMm: windowDraft.body.near_mm,
        maxWaterMm: windowDraft.body.max_water_mm,
      }
    : null;
  const searchNew = windowNew ? gateAt(windowNew) : null;
  const windowChanged =
    !windowDraft.body ||
    windowNew.nearMm !== windowNow.nearMm ||
    windowNew.maxWaterMm !== windowNow.maxWaterMm;
  const span = (g) => `${Math.round(g.from).toLocaleString()}–${mm(g.to, 1)}`;
  const emptyText = (g) =>
    `The search window would be empty: it would start at ${mm(g.from, 1)}, not nearer the radar than its end at ${mm(g.to, 1)} (the floor and its margin). Search from nearer the radar, or allow deeper water.`;
  // The lengths and notes typed above are not saved with the window.
  const lengthsEdited =
    !!facts &&
    (pipeText.trim() !== lengthText(facts.pipeLengthMm) ||
      floorText.trim() !== lengthText(facts.expectedFloorMm) ||
      notesText.trim() !== facts.notes);
  const saveWindow = async () => {
    setDone(null);
    setAsking(null);
    if (windowDraft.error) {
      setWindowError(windowDraft.error);
      return;
    }
    if (searchNew.empty) {
      setWindowError(emptyText(searchNew));
      return;
    }
    setBusy("window");
    setWindowError("");
    try {
      const data = await rtctApi.learnRadarProfile(mac, {
        pipe_length_mm: facts.pipeLengthMm,
        ...(facts.expectedFloorMm !== null
          ? { expected_floor_mm: facts.expectedFloorMm }
          : {}),
        ...(typeof profile.notes === "string" && profile.notes
          ? { notes: profile.notes }
          : {}),
        ...windowDraft.body,
        confirm: true,
      });
      if (!alive.current) return;
      const saved = readProfile(data);
      const savedFacts = profileFacts(saved);
      setLearned(saved);
      setLearnedGate(readSuggestedGate(data));
      setNearText(lengthText(savedFacts?.nearMm ?? null));
      setDeepText(lengthText(savedFacts?.maxWaterMm ?? null));
      setDone({
        kind: "window",
        window: {
          nearMm: savedFacts?.nearMm ?? null,
          maxWaterMm: savedFacts?.maxWaterMm ?? null,
        },
        kept: !!savedFacts?.canUndo,
      });
      setTick((n) => n + 1);
      onLearned?.();
    } catch (e) {
      if (alive.current)
        setWindowError(
          `The search window was not saved: ${e.message || "the server refused it."}`,
        );
    } finally {
      if (alive.current) setBusy("");
    }
  };

  // ---- The soil surface (the backend's soil support) ----
  // Saved alone, as the window is: the profile's lengths and notes as the
  // server keeps them, no dry window (so the map is kept) and the soil
  // surface, null for none. The server gives each reading against it as it
  // serves it: nothing is analysed again.
  const soilKept = !!facts?.soilKept;
  const soilDraft = soilPayload(soilText, {
    expectedFloorMm: facts?.expectedFloorMm ?? null,
    pipeLengthMm: facts?.pipeLengthMm ?? null,
  });
  const soilNow = facts?.soilAboveFloorMm ?? null;
  const soilNew = soilDraft.body ? soilDraft.body.soil_above_floor_mm : null;
  const soilChanged = !soilDraft.body || soilNew !== soilNow;
  // The soil surface below the radar for a soil surface over the floor: the
  // expected floor less it.
  const soilAirText = (above) =>
    `${mm(facts.expectedFloorMm - above, 1)} below the radar (the expected floor, ${mm(facts.expectedFloorMm, 1)}, less ${mm(above, 1)})`;
  const soilStatus = !facts
    ? ""
    : facts.expectedFloorMm === null
      ? "The soil surface is measured from the expected floor: save one above first, or readings cannot be given against the soil."
      : `${soilNow !== null ? `The soil surface is ${soilAirText(soilNow)} now` : "No soil surface yet: readings are not given against the soil"}${
          soilChanged && soilDraft.body
            ? soilNew !== null
              ? `; with this, ${soilAirText(soilNew)}`
              : "; with this, none"
            : ""
        }.`;
  const saveSoil = async () => {
    setDone(null);
    setAsking(null);
    if (soilDraft.error) {
      setSoilError(soilDraft.error);
      return;
    }
    setBusy("soil");
    setSoilError("");
    try {
      const data = await rtctApi.learnRadarProfile(mac, {
        pipe_length_mm: facts.pipeLengthMm,
        ...(facts.expectedFloorMm !== null
          ? { expected_floor_mm: facts.expectedFloorMm }
          : {}),
        ...(typeof profile.notes === "string" && profile.notes
          ? { notes: profile.notes }
          : {}),
        ...soilDraft.body,
        confirm: true,
      });
      if (!alive.current) return;
      const saved = readProfile(data);
      const savedFacts = profileFacts(saved);
      setLearned(saved);
      setLearnedGate(readSuggestedGate(data));
      setSoilText(lengthText(savedFacts?.soilAboveFloorMm ?? null));
      setTopText(lengthText(savedFacts?.pipeAboveSoilMm ?? null));
      setDone({
        kind: "soil",
        aboveFloorMm: savedFacts?.soilAboveFloorMm ?? null,
        soil: soilReference(savedFacts),
        kept: !!savedFacts?.canUndo,
      });
      setTick((n) => n + 1);
      onLearned?.();
    } catch (e) {
      if (alive.current)
        setSoilError(
          `The soil surface was not saved: ${e.message || "the server refused it."}`,
        );
    } finally {
      if (alive.current) setBusy("");
    }
  };

  // The pipe's measured height over the soil: when given, the soil reference is taken from the top of the pipe (less
  // the radar zero) instead of the floor, since the soil left inside a pipe varies with how its hole was dug.
  const topKept = !!facts?.pipeAboveKept;
  const topDraft = topPayload(topText, { pipeLengthMm: facts?.pipeLengthMm ?? null });
  const topNow = facts?.pipeAboveSoilMm ?? null;
  const topNew = topDraft.body ? topDraft.body.pipe_above_soil_mm : null;
  const topChanged = !topDraft.body || topNew !== topNow;
  const zero = facts?.radarZeroMm ?? 20;
  const topAirText = (top) =>
    `${mm(top - zero, 1)} below the radar (${mm(top, 1)} less the radar's ${mm(zero, 1)} inside the top${facts?.radarZeroMeasured ? "" : ", assumed"})`;
  const topStatus = !facts
    ? ""
    : `${topNow !== null ? `The soil surface is taken from the top of the pipe: ${topAirText(topNow)}` : "Not measured: the soil surface is taken from the floor, as above"}${
        topChanged && topDraft.body
          ? topNew !== null
            ? `; with this, ${topAirText(topNew)}`
            : "; with this, from the floor again"
          : ""
      }.`;
  const saveTop = async () => {
    setDone(null);
    setAsking(null);
    if (topDraft.error) {
      setTopError(topDraft.error);
      return;
    }
    setBusy("top");
    setTopError("");
    try {
      const data = await rtctApi.learnRadarProfile(mac, {
        pipe_length_mm: facts.pipeLengthMm,
        ...(facts.expectedFloorMm !== null ? { expected_floor_mm: facts.expectedFloorMm } : {}),
        ...(typeof profile.notes === "string" && profile.notes ? { notes: profile.notes } : {}),
        ...topDraft.body,
        confirm: true,
      });
      if (!alive.current) return;
      const saved = readProfile(data);
      const savedFacts = profileFacts(saved);
      setLearned(saved);
      setLearnedGate(readSuggestedGate(data));
      setTopText(lengthText(savedFacts?.pipeAboveSoilMm ?? null));
      setDone({
        kind: "top",
        pipeAboveSoilMm: savedFacts?.pipeAboveSoilMm ?? null,
        soil: soilReference(savedFacts),
        kept: !!savedFacts?.canUndo,
      });
      setTick((n) => n + 1);
      onLearned?.();
    } catch (e) {
      if (alive.current) setTopError(`The pipe's height was not saved: ${e.message || "the server refused it."}`);
    } finally {
      if (alive.current) setBusy("");
    }
  };

  const warnings = facts?.quality?.warnings || [];
  const [chip, tone] = missing
    ? ["Not available yet", "is-off"]
    : facts?.commissioned
      ? [
          `Commissioned${facts.bursts !== null ? ` · ${facts.bursts.toLocaleString()} burst${facts.bursts === 1 ? "" : "s"}` : ""}${warnings.length ? ` · ${warnings.length} warning${warnings.length === 1 ? "" : "s"}` : ""}`,
          warnings.length ? "is-waiting" : "is-running",
        ]
      : facts
        ? ["Lengths only, no map yet", "is-waiting"]
        : loaded.loaded
          ? ["Not learned yet", "is-waiting"]
          : ["Checking", "is-off"];
  const echoText = (e) =>
    `${mm(e.mm)}${e.db !== null ? ` · ${e.db.toFixed(1)} dB` : ""}${e.snr !== null ? ` · ${e.snr.toFixed(1)} dB over the noise` : ""}`;
  const tooFew = !picked || picked.count < LEARN_MIN;
  const when = (iso) => (iso ? sgt(iso) : null);

  // The confirm: the profile in use (its map's bursts and window), what it
  // becomes, and how to go back.
  const undoTarget =
    facts?.revisions.find((r) => r.reason === "replaced") || null;
  const sent =
    asking && asking !== "undo" ? payloadOf(asking === "learn").body : null;
  // Learning or saving the lengths keeps the search window.
  const becomes = sent
    ? {
        pipeLengthMm: sent.pipe_length_mm ?? null,
        expectedFloorMm: sent.expected_floor_mm ?? null,
        ...windowNow,
        soilAboveFloorMm: soilNow,
      }
    : null;
  const burstsText =
    !picked || !range
      ? ""
      : thinned
        ? `every burst from ${sgt(points[range.lo].t)} to ${sgt(points[range.hi].t)}`
        : picked.count === 1
          ? `this burst, ${sgt(points[range.lo].t)}`
          : `these ${picked.count} bursts, ${sgt(points[range.lo].t)} to ${sgt(points[range.hi].t)}`;
  const inUse = facts
    ? `In use now: ${profileText(facts)}.`
    : "No profile is in use yet.";
  const kept = facts?.keepsRevisions
    ? "The profile in use is kept: Undo puts it back."
    : null;
  const confirm =
    asking === "learn" && becomes
      ? {
          question: facts
            ? "Replace the empty-pipe profile in use?"
            : "Learn the empty pipe from these bursts?",
          items: [
            inUse,
            `${facts ? "It becomes" : "The profile"}: a map learned from ${burstsText} · ${lengthsText(becomes)}.`,
            <>
              <b>Only commission a DRY, EMPTY pipe.</b> A map learned with water
              in the pipe hides that water later.
              {wet > 0 &&
                ` ${wet === 1 ? "One of these bursts looks" : `${wet} of these bursts look`} wet to our analysis.`}
            </>,
            kept,
          ],
          label: facts
            ? "Confirm: replace the profile"
            : "Confirm: learn the empty pipe",
          act: () => send(true),
        }
      : asking === "save" && becomes
        ? {
            question: facts
              ? "Save the lengths and notes?"
              : "Create the profile with these lengths and notes?",
            items: [
              inUse,
              `It becomes: ${lengthsText(becomes)}, the notes as written here; ${facts?.commissioned ? "its map is kept" : "no map yet (learn one from bursts of the empty pipe)"}.`,
              kept,
            ],
            label: "Confirm: save the lengths and notes",
            act: () => send(false),
          }
        : asking === "undo" && facts
          ? {
              question: "Put back the earlier profile?",
              items: [
                inUse,
                undoTarget
                  ? `It becomes the one the change of ${sgt(undoTarget.replacedAt)}${undoTarget.by ? ` by ${undoTarget.by}` : ""} replaced: ${profileText(undoTarget)}.`
                  : "It becomes the profile the last change replaced.",
                "The profile in use now is kept among the earlier profiles, never lost; another Undo goes one more change back.",
              ],
              label: "Confirm: put the earlier profile back",
              act: undo,
            }
          : null;

  return (
    <section
      className="field-panel field-commission"
      aria-labelledby="radar-commission-title"
    >
      <div className="field-panel-head">
        <div>
          <h2 id="radar-commission-title">Commissioning: the empty pipe</h2>
          <p>
            Teach our analysis what this pipe looks like with no water in it
          </p>
        </div>
        <span className={`field-state ${tone}`} role="status">
          {chip}
        </span>
      </div>
      <div className="field-commission-grid">
        <div className="field-commission-main">
          <p className="field-commission-why">
            Our estimate compares every burst with a map of this pipe when
            empty. Learn it from bursts taken while you know the pipe was dry,
            and again after the radar or the pipe moves.
          </p>
          <More label="How the map is used" className="is-why">
            <p className="field-caption">
              From those bursts the server keeps each distance's usual level
              (the median of their spectra): every fixed echo - the rim, joints,
              the mount, the radar's own near field - and the floor. Later, an
              echo where the map has one is clutter, not water; water has to
              stand nearer than the floor and clearly stronger, allowing for
              range; and while the floor still answers at its usual level, there
              is no water over it. Without a map the analysis learns fixed
              echoes from the node's own recent bursts, with less confidence.
            </p>
          </More>
          {missing ? (
            <div className="field-notice">
              Commissioning needs the server's radar analysis (GET and POST
              radar/profile/), which this server does not provide yet.
            </div>
          ) : (
            <>
              {loaded.error && loaded.error.status !== 404 && (
                <div className="field-alert" role="alert">
                  The profile could not load: {loaded.error.message}
                </div>
              )}
              <div className="field-commission-inputs">
                <label htmlFor="radar-commission-pipe">
                  Pipe length (mm)
                  <small>
                    From the radar's face to the bottom of the pipe; needed,{" "}
                    {PIPE_MM[0]}–{PIPE_MM[1].toLocaleString()}
                  </small>
                  <input
                    id="radar-commission-pipe"
                    type="number"
                    inputMode="numeric"
                    min={PIPE_MM[0]}
                    max={PIPE_MM[1]}
                    step={1}
                    required
                    placeholder="e.g. 1200"
                    value={pipeText}
                    onChange={(e) => {
                      setPipeText(e.target.value);
                      setError("");
                      setAsking(null);
                    }}
                  />
                </label>
                <label htmlFor="radar-commission-floor">
                  Expected floor (mm)
                  <small>
                    Taped from the radar's face, as distance reads; optional,{" "}
                    {FLOOR_MM[0]}–{FLOOR_MM[1].toLocaleString()}
                  </small>
                  <input
                    id="radar-commission-floor"
                    type="number"
                    inputMode="numeric"
                    min={FLOOR_MM[0]}
                    max={FLOOR_MM[1]}
                    step={1}
                    placeholder="e.g. 1150"
                    value={floorText}
                    onChange={(e) => {
                      setFloorText(e.target.value);
                      setError("");
                      setAsking(null);
                    }}
                  />
                </label>
                <label
                  htmlFor="radar-commission-notes"
                  className="field-commission-notes"
                >
                  Notes
                  <small>
                    Optional: how you know the pipe was empty, what is in it ·{" "}
                    {notesText.length}/{NOTES_MAX}
                  </small>
                  <textarea
                    id="radar-commission-notes"
                    rows={2}
                    maxLength={NOTES_MAX}
                    placeholder="e.g. Pipe checked empty at 10:00; a bracket at about 120 mm"
                    value={notesText}
                    onChange={(e) => {
                      setNotesText(e.target.value);
                      setError("");
                      setAsking(null);
                    }}
                  />
                </label>
              </div>
              <div
                className="field-commission-picker"
                role="group"
                aria-labelledby="radar-commission-bursts"
              >
                <h3
                  id="radar-commission-bursts"
                  className="field-radar-heading"
                >
                  Bursts taken while the pipe was empty
                </h3>
                {!points.length || !range ? (
                  <div className="field-radar-empty">
                    No analysed bursts in the last {windowText(minutes)} to
                    learn from. Choose a longer window above, or turn bursts on
                    in{" "}
                    <Link to="/node-control">
                      Radar rich data on Node Control
                    </Link>
                    .
                  </div>
                ) : (
                  <>
                    <Strip
                      points={points}
                      from={from}
                      to={to}
                      lo={range.lo}
                      hi={range.hi}
                    />
                    <div className="field-commission-sliders">
                      <label htmlFor="radar-commission-first">
                        First burst
                        <input
                          id="radar-commission-first"
                          type="range"
                          min={0}
                          max={points.length - 1}
                          step={1}
                          value={range.lo}
                          aria-valuetext={sgt(points[range.lo].t)}
                          onChange={(e) => {
                            const lo = Number(e.target.value);
                            choose(lo, Math.max(lo, range.hi));
                          }}
                        />
                      </label>
                      <label htmlFor="radar-commission-last">
                        Last burst
                        <input
                          id="radar-commission-last"
                          type="range"
                          min={0}
                          max={points.length - 1}
                          step={1}
                          value={range.hi}
                          aria-valuetext={sgt(points[range.hi].t)}
                          onChange={(e) => {
                            const hi = Number(e.target.value);
                            choose(Math.min(range.lo, hi), hi);
                          }}
                        />
                      </label>
                    </div>
                    <p className="field-commission-range" role="status">
                      {picked.count === 1
                        ? `One burst${thinned ? " shown" : ""}, ${sgt(points[range.lo].t)}`
                        : `${picked.count} bursts${thinned ? " shown" : ""}, ${sgt(points[range.lo].t)} to ${sgt(points[range.hi].t)}`}
                      {Object.keys(picked.states).length
                        ? ` · ${Object.entries(picked.states)
                            .map(
                              ([state, n]) =>
                                `${n} ${STATES[state].label.toLowerCase()}`,
                            )
                            .join(", ")} by our analysis now`
                        : ""}
                    </p>
                    {wet > 0 && (
                      <div className="field-radar-caution" role="note">
                        {wet === 1
                          ? "One of these bursts looks"
                          : `${wet} of these bursts look`}{" "}
                        wet to our analysis. Learn only from bursts taken while
                        the pipe was empty: water in the map would hide water
                        later.
                      </div>
                    )}
                    {thinned && (
                      <small className="field-radar-hint">
                        The timeline shows some of the window's bursts (it is
                        thinned out); the server learns from every burst between
                        the two times. A day's window shows them all.
                      </small>
                    )}
                    {before && !chosen && (
                      <small className="field-radar-hint">
                        The profile now was learned from bursts before the last{" "}
                        {windowText(minutes)}; choose a longer window above to
                        see them.
                      </small>
                    )}
                    <small className="field-radar-hint">
                      {picked.count < LEARN_MIN
                        ? `The server learns from ${LEARN_MIN} bursts or more; choose more.`
                        : "Ten bursts or more (an hour or two of bursts) give a steadier map; the server uses at most 200, evenly spread."}
                    </small>
                  </>
                )}
              </div>
              {error && (
                <div className="field-alert" role="alert">
                  {error}
                </div>
              )}
              {done && done.kind !== "window" && done.kind !== "soil" && (
                <div className="field-notice" role="status">
                  {done.kind === "undo"
                    ? `Put back the profile ${done.replacedAt ? `replaced ${sgt(done.replacedAt)}` : "the last change replaced"}${done.bursts > 0 ? `, its map from ${plural(done.bursts, "burst")}` : done.bursts === 0 ? ", with no map (lengths only)" : ""}.`
                    : done.kind === "learn"
                      ? `${done.existed ? "Commissioned again" : "Commissioned"}${done.bursts !== null ? `: the map is learned from ${plural(done.bursts, "burst")}` : ""}.`
                      : `${done.existed ? "Saved" : "Profile created"}: the lengths and notes; a map learned before is kept.`}
                  {done.kept &&
                    " The profile it replaced is kept: Undo puts it back."}{" "}
                  The server now analyses the bursts again with this profile (a
                  few each time the page asks, or all at once with
                  rtct_radar_analyse), so the timeline above may take a minute
                  to refresh.
                </div>
              )}
              <div className="field-actions" style={{ marginTop: 0 }}>
                <button
                  type="button"
                  className="field-button primary"
                  disabled={!!busy || tooFew}
                  aria-expanded={asking === "learn"}
                  onClick={() => ask("learn")}
                >
                  {busy === "learn"
                    ? "Learning…"
                    : thinned
                      ? "Learn the empty pipe from the bursts between these times"
                      : `Learn the empty pipe from ${picked?.count === 1 ? "this burst" : `these ${picked?.count ?? 0} bursts`}`}
                </button>
                <button
                  type="button"
                  className="field-button"
                  disabled={!!busy}
                  aria-expanded={asking === "save"}
                  onClick={() => ask("save")}
                >
                  {busy === "save"
                    ? "Saving…"
                    : "Save the lengths and notes only"}
                </button>
                {facts?.canUndo && (
                  <button
                    type="button"
                    className="field-button"
                    disabled={!!busy}
                    aria-expanded={asking === "undo"}
                    onClick={() => ask("undo")}
                  >
                    {busy === "undo"
                      ? "Putting it back…"
                      : "Undo the last change"}
                  </button>
                )}
              </div>
              {confirm && (
                <div
                  className="field-radar-confirm field-commission-confirm"
                  role="group"
                  aria-labelledby="radar-commission-confirm"
                >
                  <p id="radar-commission-confirm">
                    <b>{confirm.question}</b>
                  </p>
                  <ul>
                    {confirm.items.filter(Boolean).map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                    <li>
                      The server then analyses the bursts again with{" "}
                      {asking === "undo" ? "the profile put back" : "it"}.
                    </li>
                  </ul>
                  <div className="field-actions" style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      className="field-button primary"
                      disabled={!!busy}
                      onClick={confirm.act}
                    >
                      {busy ? "Sending…" : confirm.label}
                    </button>
                    <button
                      ref={cancelButton}
                      type="button"
                      className="field-button"
                      disabled={!!busy}
                      onClick={() => setAsking(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {facts?.updatedAt && (
                <small className="field-radar-hint">
                  Either replaces the profile saved {sgt(facts.updatedAt)};
                  saving without bursts keeps its map.
                </small>
              )}
              <div
                className="field-commission-window"
                role="group"
                aria-labelledby="radar-commission-window"
              >
                <h3
                  id="radar-commission-window"
                  className="field-radar-heading"
                >
                  Search window
                </h3>
                <p className="field-caption" style={{ margin: 0 }}>
                  Where our estimate looks for the level in this pipe. Saving it
                  keeps the empty-pipe map; Undo puts the earlier window back.
                </p>
                {!windowKept ? (
                  loaded.loaded &&
                  !loaded.error && (
                    <div className="field-notice">
                      The search window needs the backend's .62 support, which
                      this server does not have yet.
                    </div>
                  )
                ) : !facts ? (
                  <p className="field-caption" style={{ margin: 0 }}>
                    Save the pipe's lengths first, or learn the empty pipe: the
                    window is kept on the node's profile.
                  </p>
                ) : (
                  <>
                    <div className="field-commission-inputs">
                      <label htmlFor="radar-commission-near">
                        Search from (mm below the radar)
                        <small>
                          Never the level: the mount, the hat, the near field.
                          Blank: the global setting, {globalNear} mm;{" "}
                          {NEAR_MM[0]}–{NEAR_MM[1].toLocaleString()}
                        </small>
                        <input
                          id="radar-commission-near"
                          type="number"
                          inputMode="numeric"
                          min={NEAR_MM[0]}
                          max={NEAR_MM[1]}
                          step={1}
                          placeholder={`${globalNear} (the global setting)`}
                          value={nearText}
                          onChange={(e) => {
                            setNearText(e.target.value);
                            setWindowError("");
                          }}
                        />
                      </label>
                      <label htmlFor="radar-commission-deep">
                        Deepest water (mm above the floor)
                        <small>
                          The search starts no nearer the radar than the floor
                          less this. Blank: no limit; {MAX_WATER_MM[0]}–
                          {MAX_WATER_MM[1].toLocaleString()}
                        </small>
                        <input
                          id="radar-commission-deep"
                          type="number"
                          inputMode="numeric"
                          min={MAX_WATER_MM[0]}
                          max={MAX_WATER_MM[1]}
                          step={1}
                          placeholder="no limit"
                          value={deepText}
                          onChange={(e) => {
                            setDeepText(e.target.value);
                            setWindowError("");
                          }}
                        />
                      </label>
                    </div>
                    <p className="field-commission-range" role="status">
                      {`The search runs ${span(searchNow)} now${searchText(windowNow) ? ` (${searchText(windowNow)})` : " (the global setting)"}`}
                      {windowChanged && searchNew
                        ? searchNew.empty
                          ? "; with these it would be empty"
                          : `; with these, ${span(searchNew)}`
                        : ""}
                      .
                    </p>
                    {windowChanged && lengthsEdited && (
                      <small className="field-radar-hint">
                        The lengths and notes above are not saved with the
                        window: Save the lengths and notes only saves them.
                      </small>
                    )}
                    {windowError && (
                      <div className="field-alert" role="alert">
                        {windowError}
                      </div>
                    )}
                    {done?.kind === "window" && (
                      <div className="field-notice" role="status">
                        Saved the search window:{" "}
                        {searchText(done.window) || "the global setting"}. The
                        empty-pipe map, the lengths and the notes are kept
                        {done.kept ? "; Undo puts the earlier window back" : ""}
                        . The server now analyses the bursts again with it (a
                        few each time the page asks, or all at once with
                        rtct_radar_analyse), so the timeline above may take a
                        minute to refresh.
                      </div>
                    )}
                    <div className="field-actions" style={{ marginTop: 0 }}>
                      <button
                        type="button"
                        className="field-button"
                        disabled={!!busy || !windowChanged}
                        onClick={saveWindow}
                      >
                        {busy === "window"
                          ? "Saving…"
                          : "Save the search window"}
                      </button>
                    </div>
                  </>
                )}
                {gate && (
                  <div className="field-notice" role="note">
                    <b>
                      On-node estimate: search {gateText(gate.min, gate.max)}.
                    </b>{" "}
                    {gate.matches === true ? (
                      "The node already searches the same bins: nothing to push."
                    ) : (
                      <>
                        Push it to the node from{" "}
                        <Link to="/node-control#field-rich-edge">
                          Radar rich data on Node Control
                        </Link>{" "}
                        (On-node estimate: Apply the suggested gate, then send
                        it).
                      </>
                    )}
                    <More label="Why this gate?" className="is-why">
                      <p className="field-caption">
                        {gateBasisText(gate)} {gateMatchText(gate)} The node
                        runs its own estimate from firmware rtct.62.
                      </p>
                    </More>
                  </div>
                )}
              </div>
              <div
                className="field-commission-window"
                role="group"
                aria-labelledby="radar-commission-soil"
              >
                <h3 id="radar-commission-soil" className="field-radar-heading">
                  Soil surface
                </h3>
                <p className="field-caption" style={{ margin: 0 }}>
                  Where the field's soil meets this pipe, so that each reading
                  is given against it: the water's depth that alternate wetting
                  and drying (AWD) irrigates by. Saving it keeps the empty-pipe
                  map and every analysis; Undo puts the earlier one back.
                </p>
                {!facts ? (
                  <p className="field-caption" style={{ margin: 0 }}>
                    Save the pipe's lengths first, or learn the empty pipe: the
                    soil surface is kept on the node's profile.
                  </p>
                ) : !soilKept ? (
                  <div className="field-notice">
                    The soil surface needs the backend's soil support, which
                    this server does not have yet.
                  </div>
                ) : (
                  <>
                    <div className="field-commission-inputs">
                      <label htmlFor="radar-commission-soil-mm">
                        Soil surface above the pipe's floor (mm)
                        <small>
                          From the pipe's bottom up to the soil around it.
                          Blank: none; {SOIL_MM[0]}–
                          {SOIL_MM[1].toLocaleString()}
                        </small>
                        <input
                          id="radar-commission-soil-mm"
                          type="number"
                          inputMode="numeric"
                          min={SOIL_MM[0]}
                          max={SOIL_MM[1]}
                          step={1}
                          placeholder="none"
                          value={soilText}
                          onChange={(e) => {
                            setSoilText(e.target.value);
                            setSoilError("");
                          }}
                        />
                      </label>
                    </div>
                    <p className="field-commission-range" role="status">
                      {soilStatus}
                    </p>
                    {soilChanged && lengthsEdited && (
                      <small className="field-radar-hint">
                        The lengths and notes above are not saved with the soil
                        surface: Save the lengths and notes only saves them.
                      </small>
                    )}
                    {soilError && (
                      <div className="field-alert" role="alert">
                        {soilError}
                      </div>
                    )}
                    {done?.kind === "soil" && (
                      <div className="field-notice" role="status">
                        {done.aboveFloorMm !== null
                          ? `Saved the soil surface: ${mm(done.aboveFloorMm)} above the pipe's floor${done.soil ? `, ${mm(done.soil.airMm, 1)} below the radar` : ""}.`
                          : "Cleared the soil surface: readings are no longer given against the soil."}{" "}
                        The empty-pipe map, the lengths, the notes and the
                        analyses are kept
                        {done.kept
                          ? "; Undo puts the earlier profile back"
                          : ""}
                        .
                      </div>
                    )}
                    <div className="field-actions" style={{ marginTop: 0 }}>
                      <button
                        type="button"
                        className="field-button"
                        disabled={!!busy || !soilChanged}
                        onClick={saveSoil}
                      >
                        {busy === "soil" ? "Saving…" : "Save the soil surface"}
                      </button>
                    </div>
                    {topKept && (
                      <>
                        <div className="field-commission-inputs">
                          <label htmlFor="radar-commission-top-mm">
                            Pipe above the soil (mm)
                            <small>
                              Measured with a tape from the soil to the pipe's top edge. When given, the soil
                              surface is taken from it instead of the floor. Blank: not measured
                            </small>
                            <input
                              id="radar-commission-top-mm"
                              type="number"
                              inputMode="numeric"
                              min={PIPE_ABOVE_SOIL_MM[0]}
                              max={PIPE_ABOVE_SOIL_MM[1]}
                              step={1}
                              placeholder="not measured"
                              value={topText}
                              onChange={(e) => {
                                setTopText(e.target.value);
                                setTopError("");
                              }}
                            />
                          </label>
                        </div>
                        <p className="field-commission-range" role="status">
                          {topStatus}
                        </p>
                        {topError && (
                          <div className="field-alert" role="alert">
                            {topError}
                          </div>
                        )}
                        {done?.kind === "top" && (
                          <div className="field-notice" role="status">
                            {done.pipeAboveSoilMm !== null
                              ? `Saved: the pipe stands ${mm(done.pipeAboveSoilMm)} above the soil${done.soil ? `, so the soil surface is ${mm(done.soil.airMm, 1)} below the radar` : ""}.`
                              : "Cleared: the soil surface is taken from the floor again."}{" "}
                            Nothing is analysed again{done.kept ? "; Undo puts the earlier profile back" : ""}.
                          </div>
                        )}
                        <div className="field-actions" style={{ marginTop: 0 }}>
                          <button
                            type="button"
                            className="field-button"
                            disabled={!!busy || !topChanged}
                            onClick={saveTop}
                          >
                            {busy === "top" ? "Saving…" : "Save the pipe's height"}
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
        <div className="field-commission-side">
          <h3 className="field-radar-heading">The profile now</h3>
          {facts ? (
            <>
              <dl className="field-facts field-stream-facts">
                {[
                  ["Saved", when(facts.updatedAt)],
                  [
                    "Map learned from the bursts of",
                    facts.from && facts.to
                      ? `${sgt(facts.from)} to ${sgt(facts.to)}`
                      : null,
                  ],
                  [
                    "Bursts in the map",
                    !facts.commissioned
                      ? "No map yet"
                      : facts.bursts !== null
                        ? facts.bursts.toLocaleString()
                        : null,
                  ],
                  [
                    "Floor echo in the map",
                    facts.quality
                      ? facts.quality.floor
                        ? echoText(facts.quality.floor)
                        : "None found near the expected floor"
                      : null,
                  ],
                  [
                    "Noise floor",
                    facts.quality?.noiseDb !== null &&
                    facts.quality?.noiseDb !== undefined
                      ? `${facts.quality.noiseDb.toFixed(1)} dB`
                      : null,
                  ],
                  [
                    "Pipe length",
                    facts.pipeLengthMm !== null ? mm(facts.pipeLengthMm) : null,
                  ],
                  [
                    "Expected floor",
                    facts.expectedFloorMm !== null
                      ? mm(facts.expectedFloorMm)
                      : "Not given",
                  ],
                  [
                    "Search window",
                    windowKept
                      ? searchText(facts) ||
                        `The global setting: from ${globalNear} mm, no deepest water`
                      : null,
                  ],
                  [
                    "Soil surface",
                    soilKept
                      ? facts.soilAboveFloorMm !== null
                        ? `${mm(facts.soilAboveFloorMm)} above the floor${facts.soilAirMm !== null ? ` · ${mm(facts.soilAirMm, 1)} below the radar` : ""}`
                        : "Not given"
                      : null,
                  ],
                ]
                  .filter(([, value]) => value)
                  .map(([label, value]) => (
                    <div className="field-fact" key={label}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
              </dl>
              {facts.quality?.echoes.length > 0 && (
                <div className="field-commission-echoes">
                  <span className="field-live-label">
                    Echoes inside the pipe, in the map
                  </span>
                  <ul aria-label="Echoes in the map">
                    {facts.quality.echoes.map((e) => (
                      <li key={e.mm}>{echoText(e)}</li>
                    ))}
                  </ul>
                </div>
              )}
              {warnings.length > 0 && (
                <div className="field-radar-caution" role="note">
                  <b>Check before trusting the map</b>
                  <ul
                    className="field-analysis-reasons"
                    aria-label="Profile warnings"
                  >
                    {warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
              {facts.notes && (
                <p className="field-caption field-commission-note">
                  <b>Notes</b> {facts.notes}
                </p>
              )}
              {facts.revisions.length > 0 && (
                <div className="field-commission-history">
                  <span className="field-live-label">
                    Earlier profiles, newest first
                  </span>
                  <ol aria-label="Earlier profiles">
                    {facts.revisions.map((r, i) => (
                      <li key={`${r.replacedAt}-${i}`}>
                        <b>
                          {r.reason === "undone"
                            ? `Taken off by an undo ${sgt(r.replacedAt)}`
                            : `Replaced ${sgt(r.replacedAt)}`}
                          {r.by ? ` by ${r.by}` : ""}
                        </b>
                        <span>{profileText(r)}</span>
                        {r === undoTarget && (
                          <small>Undo puts this one back</small>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              <details className="field-radar-details">
                <summary>The profile as the server keeps it</summary>
                <pre className="field-raw">
                  {JSON.stringify(profile, null, 2)}
                </pre>
              </details>
            </>
          ) : (
            <p className="field-caption" style={{ margin: 0 }}>
              {missing
                ? "No profile: this server does not keep one yet."
                : loaded.loaded
                  ? "Not learned yet. Until it is, our estimate cannot tell a fixed echo in the pipe from water, and says so."
                  : "Loading the profile…"}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
